/*
  Global JavaScript - Redline Auto Parts
  Core functionality for the automotive parts theme
*/

// Cart functionality
class CartManager {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateCartCount();
  }

  bindEvents() {
    // Add to cart buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-add-to-cart]')) {
        this.handleAddToCart(e);
      }
    });

    // Cart quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-quantity]')) {
        this.handleQuantityChange(e);
      }
    });

    // Remove from cart
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-remove-from-cart]')) {
        this.handleRemoveFromCart(e);
      }
    });
  }

  async handleAddToCart(event) {
    event.preventDefault();
    
    const button = event.target.closest('[data-add-to-cart]');
    const form = button.closest('form');
    
    if (!form) return;

    const formData = new FormData(form);
    
    try {
      button.classList.add('loading');
      button.disabled = true;

      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const cart = await this.getCart();
      this.updateCartUI(cart);
      this.showNotification('Product added to cart', 'success');

    } catch (error) {
      console.error('Add to cart error:', error);
      this.showNotification('Failed to add product to cart', 'error');
    } finally {
      button.classList.remove('loading');
      button.disabled = false;
    }
  }

  async handleQuantityChange(event) {
    const input = event.target;
    const line = input.dataset.line;
    const quantity = parseInt(input.value);

    if (isNaN(quantity) || quantity < 0) return;

    try {
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const cart = await response.json();
      this.updateCartUI(cart);

    } catch (error) {
      console.error('Cart update error:', error);
      this.showNotification('Failed to update cart', 'error');
    }
  }

  async handleRemoveFromCart(event) {
    event.preventDefault();
    
    const button = event.target.closest('[data-remove-from-cart]');
    const line = button.dataset.line;

    try {
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: 0
        })
      });

      if (!response.ok) {
        throw new Error('Failed to remove from cart');
      }

      const cart = await response.json();
      this.updateCartUI(cart);
      this.showNotification('Product removed from cart', 'success');

    } catch (error) {
      console.error('Remove from cart error:', error);
      this.showNotification('Failed to remove product', 'error');
    }
  }

  async getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      console.error('Get cart error:', error);
      return null;
    }
  }

  updateCartUI(cart) {
    if (!cart) return;

    // Update cart count
    this.updateCartCount(cart.item_count);

    // Dispatch cart updated event
    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { cart }
    }));
  }

  updateCartCount(count = null) {
    if (count === null) {
      // Get count from existing element or fetch cart
      const cartCountElement = document.getElementById('cart-count');
      count = cartCountElement ? parseInt(cartCountElement.textContent) || 0 : 0;
    }

    const cartCountElements = document.querySelectorAll('[data-cart-count]');
    cartCountElements.forEach(element => {
      element.textContent = count;
      element.style.display = count > 0 ? 'flex' : 'none';
    });

    // Update cart icon
    const cartCountElement = document.getElementById('cart-count');
    if (cartCountElement) {
      cartCountElement.textContent = count;
      cartCountElement.style.display = count > 0 ? 'flex' : 'none';
    }
  }

  showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification--${type}`;
    notification.textContent = message;

    // Add styles if not already present
    if (!document.querySelector('#notification-styles')) {
      const styles = document.createElement('style');
      styles.id = 'notification-styles';
      styles.textContent = `
        .notification {
          position: fixed;
          top: 20px;
          right: 20px;
          padding: 1rem 1.5rem;
          border-radius: 4px;
          color: white;
          font-weight: 500;
          z-index: 10000;
          transform: translateX(100%);
          transition: transform 0.3s ease;
        }
        .notification--success {
          background-color: #4CAF50;
        }
        .notification--error {
          background-color: #F44336;
        }
        .notification--info {
          background-color: #2196F3;
        }
        .notification--show {
          transform: translateX(0);
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(notification);

    // Trigger animation
    setTimeout(() => {
      notification.classList.add('notification--show');
    }, 100);

    // Remove notification
    setTimeout(() => {
      notification.classList.remove('notification--show');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
}

// Quick View functionality
class QuickView {
  constructor() {
    this.modal = null;
    this.init();
  }

  init() {
    this.bindEvents();
    this.createModal();
  }

  bindEvents() {
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-quick-view]')) {
        this.handleQuickView(e);
      }
      
      if (e.target.closest('[data-quick-view-close]')) {
        this.closeModal();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('quick-view--open')) {
        this.closeModal();
      }
    });
  }

  createModal() {
    this.modal = document.createElement('div');
    this.modal.className = 'quick-view';
    this.modal.innerHTML = `
      <div class="quick-view__overlay" data-quick-view-close></div>
      <div class="quick-view__content">
        <button class="quick-view__close" data-quick-view-close aria-label="Close quick view">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <div class="quick-view__inner">
          <!-- Content will be loaded here -->
        </div>
      </div>
    `;

    // Add styles
    if (!document.querySelector('#quick-view-styles')) {
      const styles = document.createElement('style');
      styles.id = 'quick-view-styles';
      styles.textContent = `
        .quick-view {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
          visibility: hidden;
          opacity: 0;
          transition: all 0.3s ease;
        }
        .quick-view--open {
          visibility: visible;
          opacity: 1;
        }
        .quick-view__overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.8);
          cursor: pointer;
        }
        .quick-view__content {
          position: relative;
          background-color: white;
          border-radius: 8px;
          max-width: 90vw;
          max-height: 90vh;
          width: 1200px;
          overflow: hidden;
        }
        .quick-view__close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: white;
          border: none;
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 1;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
        .quick-view__inner {
          padding: 2rem;
          max-height: calc(90vh - 4rem);
          overflow-y: auto;
        }
        
        @media screen and (max-width: 749px) {
          .quick-view__content {
            width: 95vw;
          }
        }
      `;
      document.head.appendChild(styles);
    }

    document.body.appendChild(this.modal);
  }

  async handleQuickView(event) {
    event.preventDefault();
    event.stopPropagation();
    
    const button = event.target.closest('[data-quick-view]');
    if (!button) return;
    
    const productHandle = button.dataset.productHandle;
    
    if (!productHandle) {
      console.error('No product handle found');
      return;
    }

    try {
      console.log('Opening quick view for:', productHandle);
      
      const response = await fetch(`/products/${productHandle}?view=quick-view`);
      
      if (!response.ok) {
        console.error('Failed to fetch product:', response.status);
        return;
      }
      
      const html = await response.text();
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      const productContent = tempDiv.querySelector('.product-quick-view') || 
                             tempDiv.querySelector('.quick-view-content') ||
                             tempDiv;
      
      this.modal.querySelector('.quick-view__inner').innerHTML = productContent.innerHTML;
      this.openModal();
      
    } catch (error) {
      console.error('Quick view error:', error);
    }
  }

  openModal() {
    this.modal.classList.add('quick-view--open');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.modal.classList.remove('quick-view--open');
    document.body.style.overflow = '';
  }
}

// Image loading and lazy loading
class ImageLoader {
  constructor() {
    this.init();
  }

  init() {
    // Native lazy loading fallback for older browsers
    if ('IntersectionObserver' in window) {
      this.initLazyLoading();
    }
    
    this.initImageErrorHandling();
  }

  initLazyLoading() {
    const lazyImages = document.querySelectorAll('img[data-src]');
    
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          observer.unobserve(img);
        }
      });
    });

    lazyImages.forEach(img => imageObserver.observe(img));
  }

  initImageErrorHandling() {
    document.addEventListener('error', (e) => {
      if (e.target.tagName === 'IMG') {
        this.handleImageError(e.target);
      }
    }, true);
  }

  handleImageError(img) {
    // Add error class for styling
    img.classList.add('image-error');
    
    // Set a placeholder image or hide the image
    if (!img.dataset.fallback) {
      img.style.display = 'none';
    } else {
      img.src = img.dataset.fallback;
    }
  }
}

// Utilities
const utils = {
  debounce(func, wait, immediate) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  },

  throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    }
  },

  formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';
    
    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      
      if (isNaN(number) || number === null) {
        return 0;
      }
      
      number = (number / 100.0).toFixed(precision);
      
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      
      return dollarsAmount + centsAmount;
    }
    
    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }
    
    return formatString.replace(placeholderRegex, value);
  }
};

// Initialize everything when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize core functionality
  new CartManager();
  new QuickView();
  new ImageLoader();

  // Global utilities
  window.theme = {
    utils: utils
  };

  // Accessibility improvements
  // Skip to content link
  const skipLink = document.querySelector('.skip-to-content-link');
  if (skipLink) {
    skipLink.addEventListener('click', function(e) {
      const target = document.querySelector(this.getAttribute('href'));
      if (target) {
        target.focus();
      }
    });
  }

  // Focus management for modals and dropdowns
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Tab') {
      // Trap focus in open modals
      const openModal = document.querySelector('.quick-view--open, .mobile-menu--open');
      if (openModal) {
        const focusableElements = openModal.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
          }
        }
      }
    }
  });

  console.log('Redline Auto Parts theme initialized');
});

